import React, { useState, useEffect, useCallback } from 'react';
import { t, Trans } from '@lingui/macro';
import { debounce, sortBy } from 'lodash';

import {
  Button,
  Flex,
  H1,
  Modal,
  P,
  Pagination,
  SearchInput,
} from '@decisiv/ui-components';
import PlusCircle from '@decisiv/iconix/lib/components/PlusCircle';
import Trash from '@decisiv/iconix/lib/components/Trash';

import Helmet from '../../components/SEO/Helmet';
import WmiCodesTable from '../../components/WmiCodesTable';
import WmiCodeFormModal from '../../components/WmiCodeFormModal';
import WmiCodeHistoryModal from '../../components/WmiCodeHistoryModal';
import WrapperWithLoading from '../../components/WrapperWithLoading';
import WmiCodesApi from '../../api/wmi_codes';
import { WmiCodesSection, LineDivider, ContentSection } from './style';

const PAGE_SIZE = 25;

export default function WmiCodesPage() {
  const [wmiCodes, setWmiCodes] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  // Modal state
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [selectedWmiCode, setSelectedWmiCode] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [wmiCodeToDelete, setWmiCodeToDelete] = useState(null);

  // History modal state
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedHistoryWmiCode, setSelectedHistoryWmiCode] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const result = await WmiCodesApi.fetchWmiCodes({
      page: currentPage,
      size: PAGE_SIZE,
      search: filterSearch || null,
    });

    if (result.data) {
      const sorted = sortBy(result.data, 'attributes.oem_name');
      setWmiCodes(sorted);
      setTotalRecords(result.totalRecords ?? result.data.length);
    }
    setLoading(false);
  }, [currentPage, filterSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applySearch = useCallback(
    debounce((value) => {
      setFilterSearch(value);
      setCurrentPage(1);
    }, 500),
    [],
  );

  const onSearchInputChange = (event) => {
    setSearchInput(event.target.value);
    applySearch(event.target.value);
  };

  const totalPages = Math.ceil(totalRecords / PAGE_SIZE);

  // Create / Edit
  const handleOpenCreate = () => {
    setSelectedWmiCode(null);
    setFormModalVisible(true);
  };

  const handleEdit = (wmiCode) => {
    setSelectedWmiCode(wmiCode);
    setFormModalVisible(true);
  };

  const handleFormClose = () => {
    setFormModalVisible(false);
    setSelectedWmiCode(null);
  };

  const handleSave = async (attributes, uuid) => {
    if (uuid) {
      await WmiCodesApi.updateWmiCode(uuid, attributes);
    } else {
      await WmiCodesApi.createWmiCode(attributes);
    }
    handleFormClose();
    fetchData();
  };

  // Delete
  const handleDeleteClick = (wmiCode) => {
    setWmiCodeToDelete(wmiCode);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (wmiCodeToDelete) {
      await WmiCodesApi.deleteWmiCode(wmiCodeToDelete.id);
      setDeleteModalVisible(false);
      setWmiCodeToDelete(null);
      // Step back a page when the last row on a non-first page is removed,
      // otherwise just refresh the current page.
      if (wmiCodes.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      } else {
        fetchData();
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setWmiCodeToDelete(null);
  };

  // History
  const handleOpenHistory = (wmiCode) => {
    setSelectedHistoryWmiCode(wmiCode);
    setHistoryModalVisible(true);
  };

  const handleCloseHistory = () => {
    setHistoryModalVisible(false);
    setSelectedHistoryWmiCode(null);
  };

  return (
    <>
      <Helmet title={t`WMI Codes`} description={t`WMI Codes`} />
      <WmiCodesSection
        flexDirection="column"
        justifyContent="center"
        padding={2}
        flex="none"
        flexGrow="0"
      >
        <Flex
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex flex={1} title="WMI Codes">
            <H1>{t`WMI Codes`}</H1>
          </Flex>
          <Flex alignItems="center" style={{ gap: '1rem' }}>
            <SearchInput
              value={searchInput}
              width={24}
              label={t`Search WMI codes`}
              name="wmiCodeOemSearch"
              placeholder={t`Search by OEM name`}
              onChange={onSearchInputChange}
              size="small"
              searchDisabled={false}
            />
            <Button
              icon={PlusCircle}
              text={t`Add WMI Code`}
              onClick={handleOpenCreate}
              size="small"
            />
          </Flex>
        </Flex>
        <P shade={1}>
          {t`Define which vehicle makes (by WMI code) are eligible for each OEM. Setting VIN-based WMI validation in the Integration Global Settings Execution Conditions, you can control which assets are allowed to query external systems per integration, ensuring requests are properly scoped and only sent to relevant external systems.`}
        </P>
      </WmiCodesSection>
      <LineDivider />
      <ContentSection flexDirection="column">
        <WrapperWithLoading loading={loading}>
          <WmiCodesTable
            wmiCodes={wmiCodes}
            totalRecords={totalRecords}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            onHistory={handleOpenHistory}
          />
          {totalPages > 1 && (
            <Flex padding={2} justifyContent="flex-end">
              <Pagination
                totalPages={totalPages}
                activePage={currentPage}
                onPageChange={setCurrentPage}
              />
            </Flex>
          )}
        </WrapperWithLoading>
      </ContentSection>

      <WmiCodeFormModal
        visible={formModalVisible}
        onClose={handleFormClose}
        onSave={handleSave}
        wmiCode={selectedWmiCode}
      />

      <WmiCodeHistoryModal
        visible={historyModalVisible}
        onClose={handleCloseHistory}
        wmiCode={selectedHistoryWmiCode}
      />

      <Modal
        actions={[
          {
            text: t`Cancel`,
            onClick: handleDeleteCancel,
            kind: 'secondary',
          },
          {
            text: t`Delete`,
            onClick: handleDeleteConfirm,
            kind: 'primary',
          },
        ]}
        intent="danger"
        icon={Trash}
        title={t`Delete WMI Code`}
        visible={deleteModalVisible}
        onClose={handleDeleteCancel}
      >
        <Flex padding={1}>
          <Trans>
            Are you sure you want to delete the WMI code record for &quot;
            {wmiCodeToDelete?.attributes?.oem_name}&quot;? This action cannot be
            undone.
          </Trans>
        </Flex>
      </Modal>
    </>
  );
}
